import {
  ClinicConversationDetailSchema,
  ClinicConversationMutationSchema,
  ClinicConversationsSchema,
  ClinicMessageDirectorySchema,
  CreateClinicConversationSchema,
  SaveClinicMessageTeamSchema,
  ClinicConversationSourceSchema,
  type ClinicConversationMutation,
  type ClinicConversationsQuery,
  type CreateClinicConversation,
  type SaveClinicMessageTeam,
} from "@emr/contracts";
import type { z } from "zod";

export class ClinicMessagingError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
  get definitive() {
    return this.status >= 400 && this.status < 500 && this.status !== 408;
  }
}

export function createClinicConversationsClient(
  request: (path: string, body?: unknown, signal?: AbortSignal) => Promise<Response>,
) {
  const command = <T extends z.ZodType>(schema: T, input: unknown): z.output<T> => {
    const parsed = schema.safeParse(input);
    if (!parsed.success)
      throw new ClinicMessagingError(
        parsed.error.issues[0]?.message ?? "Check the message fields.",
        400,
      );
    return parsed.data;
  };
  async function json<T extends z.ZodType>(
    path: string,
    schema: T,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<z.output<T>> {
    const response = await request(path, body, signal);
    if (!response.ok) {
      const value = await response.json().catch(() => ({}));
      throw new ClinicMessagingError(
        value.error?.message ??
          value.message ??
          "Messaging is unavailable. Your draft has been kept.",
        response.status,
      );
    }
    return schema.parse(await response.json());
  }
  const query = (input: Record<string, unknown>) =>
    new URLSearchParams(
      Object.entries(input)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)]),
    ).toString();
  return {
    getClinicMessageDirectory: (siteId: string, signal?: AbortSignal) =>
      json(
        `/v1/clinic-conversations/directory?${query({ siteId })}`,
        ClinicMessageDirectorySchema,
        undefined,
        signal,
      ),
    setClinicMessagingEnabled: (siteId: string, enabled: boolean, idempotencyKey: string) =>
      json("/v1/clinic-conversations/settings", ClinicMessageDirectorySchema, {
        siteId,
        enabled,
        idempotencyKey,
      }),
    listClinicConversations: (input: ClinicConversationsQuery, signal?: AbortSignal) =>
      json(
        `/v1/clinic-conversations?${query(input)}`,
        ClinicConversationsSchema,
        undefined,
        signal,
      ),
    listPatientConversations: (
      siteId: string,
      patientId: string,
      cursor?: string,
      signal?: AbortSignal,
    ) =>
      json(
        `/v1/patients/${encodeURIComponent(patientId)}/conversations?${query({ siteId, cursor })}`,
        ClinicConversationsSchema,
        undefined,
        signal,
      ),
    getClinicConversation: (
      siteId: string,
      id: string,
      signal?: AbortSignal,
      beforeSequence?: number,
    ) =>
      json(
        `/v1/clinic-conversations/${encodeURIComponent(id)}?${query({ siteId, beforeSequence })}`,
        ClinicConversationDetailSchema,
        undefined,
        signal,
      ),
    getClinicConversationSource: (
      siteId: string,
      conversationId: string,
      kind: string,
      id: string,
    ) =>
      json(
        `/v1/clinic-conversations/${encodeURIComponent(conversationId)}/source?${query({ siteId, kind, sourceId: id })}`,
        ClinicConversationSourceSchema,
      ),
    getClinicConversationFile: async (
      siteId: string,
      conversationId: string,
      kind: string,
      id: string,
    ) => {
      const response = await request(
        `/v1/clinic-conversations/${encodeURIComponent(conversationId)}/source/file?${query({ siteId, kind, sourceId: id })}`,
      );
      if (!response.ok)
        throw new ClinicMessagingError(
          "Attached original is unavailable or access has changed.",
          response.status,
        );
      return response.blob();
    },
    createClinicConversation: (input: CreateClinicConversation) =>
      json(
        "/v1/clinic-conversations",
        ClinicConversationDetailSchema,
        command(CreateClinicConversationSchema, input),
      ),
    mutateClinicConversation: (id: string, input: ClinicConversationMutation) =>
      json(
        `/v1/clinic-conversations/${encodeURIComponent(id)}/commands`,
        ClinicConversationDetailSchema,
        command(ClinicConversationMutationSchema, input),
      ),
    saveClinicMessageTeam: (input: SaveClinicMessageTeam) =>
      json(
        "/v1/clinic-message-teams",
        ClinicMessageDirectorySchema,
        command(SaveClinicMessageTeamSchema, input),
      ),
  };
}
